<template>
    <!--
        Content Desired:
        - Name
        - Alarm Reading
        - Time + Readable Time
        - Status Icon
        - Type
        - Area / Zone
     -->
    <div>
        <slide-over-panel   v-model:show="showModal">
            <template #title>
                <div class="flex flex-col">
                    <span>{{ modalAlarm.name }}
                        <span class="text-sm text-gray-500">- in {{ modalAlarm.zone }}</span>
                    </span>

                    <span class="text-sm text-gray-500 ml-2">{{ modalAlarm.area }}</span>
                </div>
            </template>

            <template #content>
                <ul v-if="modalAlarm" class="m-0">
                    <!-- Alarm Reading & Type -->
                    <li class="grow px-4 py-4 sm:px-6">
                        <div class="px-2 font-semibold bg-gray-100 py-2 my-2">
                            Alarm Reading
                        </div>
                        <div class="px-3 flex flex-col">
                            <span class="font-semibold"> {{ modalAlarm.reading }}</span>
                            <span class="text-gray-500"> {{ modalAlarm.time }}</span>
                        </div>
                    </li>

                    <!-- Status -->
                    <li class="grow px-4 py-4 sm:px-6">
                        <div class="px-2 font-semibold bg-gray-100 py-2 my-2">
                            Alarm Status
                        </div>
                        <div class="px-3 flex flex-col">
                            <span class="font-semibold">
                                {{ alarmStatus(modalAlarm) }}
                            </span>
                            <span class="text-gray-500">{{ alarmTime(modalAlarm) }}</span>
                        </div>
                    </li>

                    <!-- Last Action -->
                    <li class="grow px-4 py-4 sm:px-6">
                        <div class="px-2 font-semibold bg-gray-100 py-2 my-2">
                            Last Action
                        </div>
                        <div class="px-3 flex flex-col">
                            <span class="font-semibold">
                              {{ getLastCommentActionText(modalAlarm) }} by
                              {{ getLastCommentUser(modalAlarm) }}
                            </span>
                            <span class="text-gray-500">{{ modalAlarm.last_comment_time }}</span>
                        </div>
                    </li>

                    <li class="px-4 py-8 sm:px-6 w-full">
                        <a  :href="company.buildCompanyRoute(`alarms/${modalAlarm.id}`)"
                            class="flex justify-center w-full px-4 py-2 border border-transparent text-sm
                                  leading-5 font-medium text-center rounded-md text-white bg-blue-600 hover:bg-blue-500
                                  focus:outline-none focus:ring-blue-200 focus:border-blue-700 active:bg-blue-700
                                  cursor-pointer"
                        >
                            <span>View Alarm</span>
                        </a>
                    </li>
                </ul>
            </template>
        </slide-over-panel>

        <!-- Slide over to mass address alarms using the existing address alarm components. -->
        <slide-over-panel   v-model:show="showMassAddressSideOver"
                            width="max-w-4xl">
            <template #title>
                <div class="flex flex-col">
                    Mass Address Alarms
                </div>
            </template>

            <template #content>
                <div class="flex flex-col">
                    <!-- Error Banner -->
                    <div v-if="Object.keys(massAddressErrors).length > 0" class="pb-4">
                        <banner-error v-for="(error, errorTitle) in massAddressErrors" :key="errorTitle">
                            <template #title>
                                {{ errorTitle }}
                            </template>
                            <template #content>
                                {{ error }}
                            </template>
                        </banner-error>
                    </div>

                    <!-- Address Alarm -->
                    <div    class="">
                        <!-- Render the alarm address, with events only and since this only works on unresolved alarms
                            we don't need to lock comment only.
                        -->
                        <alarm-address  :comment-only="false"
                                        :is-mass-resolving="true"
                                        :send-event-only="true"
                                        :mass-mode="true"
                                        :timezone='timezone'
                                        :require-comment="requireComment"
                                        :max-snooze-days="maxSnoozeDays"
                                        :is-snoozed="anySelectedSnoozed"
                                        @adding-comment="massAddress('comment', $event)"
                                        @snoozing-alarm="massAddress('snooze', $event)"
                                        @resolving-alarm="massAddress('resolve', $event)"
                                        @cancelling-snooze="massAddress('cancel-snooze', $event)"
                        ></alarm-address>
                    </div>

                    <!-- Cancel Button -->
                    <div class="my-2 flex flex-row justify-center">
                        <button-blue-secondary class="flex flex-row items-center justify-center"
                                              @click="showMassAddressSideOver = false"
                        >
                            Cancel
                        </button-blue-secondary>
                    </div>
                </div>
            </template>
        </slide-over-panel>

        <inline-tab-header
            :headers="filterHeaders"
            name="Filter By"
            :current="activeHeaderFilter"
            @update:current="updateCurrentFilter"
        ></inline-tab-header>

        <div class="py-4 flex flex-col gap-3">

            <!-- Row 1: Search + Location + Items Per Page -->
            <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">Search</span>
                <!-- Mobile: search+location stacked, items-per-page alongside location -->
                <!-- Desktop: search, location, spacer, items-per-page all in one row -->
                <div class="flex flex-col sm:flex-row sm:items-center gap-2">
                    <!-- Search -->
                    <div class="flex-1 sm:flex-none sm:w-56">
                        <input  type="text"
                                v-model="search"
                                placeholder="Search"
                                class="w-full px-3 py-[7px] text-sm text-gray-700 placeholder:text-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                    </div>

                    <!-- Location -->
                    <div class="location-select w-full sm:w-56">
                        <select2-group id="zone_id_group"
                                       v-model:model-value="$data._zoneIds"
                                       :allow-clear="true"
                                       :multiple="true"
                                       :close-on-select="true"
                                       :options="$data._namedAreasByZones"
                                       :selectable-groups="true"
                                       placeholder="Location"
                        ></select2-group>
                    </div>

                    <!-- Autoresolved toggle: only on resolved tab -->
                    <button v-if="activeHeaderFilter === 'resolved'"
                            @click="excludeAutoresolvedClicked"
                            class="flex-shrink-0 inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <span class="relative inline-flex items-center w-8 h-4 rounded-full transition-colors flex-shrink-0"
                              :class="!excludeAutoresolved ? 'bg-blue-500' : 'bg-gray-300'">
                            <span class="absolute inline-block w-3 h-3 bg-white rounded-full shadow transition-transform"
                                  :class="!excludeAutoresolved ? 'translate-x-4' : 'translate-x-0.5'"></span>
                        </span>
                        <span>Show Auto-Resolved</span>
                    </button>

                    <div class="flex-1 hidden sm:block"></div>

                    <div v-if="permissions.can('can_mass_alarms_address') && showMassAddressButton"
                         class="hidden lg:block flex-shrink-0">
                        <button-blue-secondary class="py-1.5 px-3 flex flex-row items-center justify-center text-sm"
                                               @click="showMassAddress">
                            Mass Address Selected Alarms
                        </button-blue-secondary>
                    </div>

                    <!-- Items per page: only visible here on desktop -->
                    <div class="hidden sm:flex flex-shrink-0 items-center gap-1.5 text-sm text-gray-500 whitespace-nowrap">
                        <select v-model="itemsPerPage"
                                class="border border-gray-300 rounded-md px-2 py-[7px] text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[4rem]">
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="75">75</option>
                            <option value="100">100</option>
                        </select>
                        <span v-if="pageInfo.total">/ {{ pageInfo.total }}</span>
                    </div>
                </div>
            </div>

            <!-- Row 2: Type filter pills -->
            <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">Type</span>
                <div class="flex flex-wrap items-center gap-2">
                <button @click="showOfflineClicked"
                        class="inline-flex items-center justify-center gap-1.5 w-[calc(33.333%-0.375rem)] sm:flex-none sm:w-32 py-2.5 sm:py-1.5 rounded-md border text-sm font-medium cursor-pointer transition-colors"
                        :class="showOffline ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'">
                    <input type="checkbox" :checked="showOffline" class="text-blue-600 rounded pointer-events-none w-3.5 h-3.5 flex-shrink-0" readonly>
                    <span>Offline</span>
                </button>

                <button @click="showThresholdClicked"
                        class="inline-flex items-center justify-center gap-1.5 w-[calc(33.333%-0.375rem)] sm:flex-none sm:w-32 py-2.5 sm:py-1.5 rounded-md border text-sm font-medium cursor-pointer transition-colors"
                        :class="showThreshold ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'">
                    <input type="checkbox" :checked="showThreshold" class="text-blue-600 rounded pointer-events-none w-3.5 h-3.5 flex-shrink-0" readonly>
                    <span>Threshold</span>
                </button>

                <button v-if="hasValidationAlarms"
                        @click="showValidationClicked"
                        class="inline-flex items-center justify-center gap-1.5 w-[calc(33.333%-0.375rem)] sm:flex-none sm:w-32 py-2.5 sm:py-1.5 rounded-md border text-sm font-medium cursor-pointer transition-colors"
                        :class="showValidation ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'">
                    <input type="checkbox" :checked="showValidation" class="text-blue-600 rounded pointer-events-none w-3.5 h-3.5 flex-shrink-0" readonly>
                    <span>Validation</span>
                </button>

                <button v-if="hasMaintenanceAlarms"
                        @click="showMaintenanceClicked"
                        class="inline-flex items-center justify-center gap-1.5 w-[calc(33.333%-0.375rem)] sm:flex-none sm:w-32 py-2.5 sm:py-1.5 rounded-md border text-sm font-medium cursor-pointer transition-colors"
                        :class="showMaintenance ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'">
                    <input type="checkbox" :checked="showMaintenance" class="text-blue-600 rounded pointer-events-none w-3.5 h-3.5 flex-shrink-0" readonly>
                    <span>Maintenance</span>
                </button>

                </div>
            </div>

            <!-- Row 3: Resolved-only controls -->
            <div v-if="activeHeaderFilter === 'resolved'" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-gray-400 uppercase tracking-wide">Time Range</span>
                <div class="flex flex-col sm:flex-row sm:items-center gap-2">
                <div class="flex flex-col w-full sm:max-w-md">
                    <div class="flex flex-row border border-gray-300 rounded-md overflow-hidden date-range-inputs">
                        <div class="w-1/2">
                            <date-time-picker   v-model="startTime"
                                                id="startPicker"
                                                placeholder="Start"
                                                :side-by-side="true"
                                                format="MM/DD/YYYY HH:mm"
                            ></date-time-picker>
                        </div>
                        <div class="w-px bg-gray-300 flex-shrink-0"></div>
                        <div class="w-1/2">
                            <date-time-picker   v-model="endTime"
                                                id="endPicker"
                                                placeholder="End"
                                                :side-by-side="true"
                                                format="MM/DD/YYYY HH:mm"
                            ></date-time-picker>
                        </div>
                    </div>
                </div>

                <download-excel :fetch="exportData" :fields="csvFields" type="csv">
                    <span v-if="exporting"
                          class="inline-flex items-center justify-center w-full sm:w-28 py-[7px] border border-blue-200 text-sm rounded-md text-blue-700 bg-gray-100 cursor-pointer">
                        <span :class="iconLoading"></span>
                        <span>Exporting</span>
                    </span>
                    <button v-else type="button"
                            class="inline-flex items-center justify-center w-full sm:w-28 py-[7px] border border-blue-300 text-sm rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none cursor-pointer">
                        Export
                    </button>
                </download-excel>
                </div>
            </div>

        </div>

        <!-- Mobile only: showing X of Y bar -->
        <div class="sm:hidden flex items-center justify-between py-2 text-sm text-gray-500">
            <span v-if="pageInfo.total">Showing {{ itemsPerPage }} alarms of {{ pageInfo.total }}</span>
            <select v-model="itemsPerPage"
                    class="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[4rem]">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
            </select>
        </div>

        <div class="bg-white overflow-hidden sm:rounded-md border">
            <ul class="m-0">
                <!-- Header Row -->
                <li>
                    <div class="block flex justify-around text-gray-500 bg-gray-50 font-semibold grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4
                        hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border-b">
                        <div    class="cursor-pointer inline-block grow flex items-center px-4 py-4 sm:px-6 "
                                @click="updateSort('name')">
                            <div    v-if="permissions.can('can_mass_alarms_address')"
                                    class="mb-2 mr-4"
                                    @click.stop>
                                <input      id="mass_address_all"
                                            v-model="massAddressAll"
                                            class="focus:ring-blue-500 rounded cursor-pointer"
                                            name="mass_address_all"
                                            type="checkbox"
                                            @change="setShouldShowMassAddress">
                            </div>
                            <span class="pr-2">Point Name</span>
                            <span v-show="sortBy ==='name'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
                        </div>
                        <div class="cursor-pointer inline-block grow px-4 py-4 sm:px-6 "
                                @click="updateSort('reading')">
                            <span class="pr-2">Alarm Reading</span>
                            <span v-show="sortBy ==='reading'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
                        </div>
                        <div class="cursor-default hidden md:inline-block grow px-4 py-4 sm:px-6 "
                                >
                            <span class="pr-2">Status</span>
                        </div>
                        <div class="cursor-pointer hidden md:inline-block grow px-4 py-4 sm:px-6 "
                                @click="updateSort('action')">
                            <span class="pr-2">Last Action</span>
                            <span v-show="sortBy ==='action'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
                        </div>
                    </div>
                </li>

                <!-- Normal Row -->
                <li v-if="loading">
                    <div class="flex flex-row items-center justify-center py-4">
                        <span :class="iconLoading"></span>
                        <span class="text-grey-500 text-base font-medium">Loading</span>
                    </div>
                </li>
                <li v-else-if="data.length > 0" v-for="(row, rowIndex) in data"
                    :class="{'bg-gray-200' : rowIndex % 2 === 1}">
                    <!-- Configure the cols by number of columns provided by default w/ lg being max -->
                    <!-- Mobile Version -->
                    <div class="block md:hidden
                        hover:bg-gray-50"
                         @click="showRowModal(data[rowIndex])">
                        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4">
                            <!-- Cell -->
                            <!-- Alarm Status, point name, zone, & Area -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div    class="flex flex-row items-center h-full">
                                    <div>
                                <span   v-if="row.status === 'active'"
                                        :class="iconAlarmActive"
                                ></span>
                                        <span   v-else-if="row.status === 'snoozed'"
                                                :class="iconAlarmSnoozed"
                                        ></span>
                                        <span   v-else-if="row.status === 'resolved'"
                                                :class="iconAlarmResolved"
                                        ></span>
                                    </div>

                                    <div class="flex flex-col content-start ml-4">
                                <span class="font-semibold">
                                    {{ row.name }}
                                    <span v-if="row.alarm_generation_schedule_label"
                                          :title="`Alarm generation schedule: ${row.alarm_generation_schedule_label}`"
                                          class="lnr icon-moon text-yellow-600 ml-1"></span>
                                    <span class="text-gray-500 font-base hidden md:inline"> - in {{ row.zone }}</span>
                                </span>
                                        <span class="text-gray-500 hidden md:inline">{{ row.area }}</span>
                                        <button v-if="row.status === 'snoozed'"
                                                type="button"
                                                @click.stop="goToUnsnooze(row)"
                                                class="mt-1 self-start px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-500 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300">
                                            Unsnooze
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Alarm Reading & Type -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold"> {{ row.reading }}</span>
                                    <span class="text-gray-500"> {{ row.time }}</span>
                                </div>
                            </div>

                            <!-- Status -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold">
                                        {{ alarmStatus(row) }}
                                    </span>
                                    <span class="text-gray-500">{{ alarmTime(row) }}</span>
                                </div>
                            </div>

                            <!-- Last Action -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                            <span class="font-semibold">
                                {{ getLastCommentActionText(row) }} by
                                {{ getLastCommentUser(row) }}
                            </span>
                                    <span class="text-gray-500">{{ row.last_comment_time }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Desktop Version -->
                    <!-- The row is a real <a> so native link behavior (middle-click / cmd-click
                         to open in a new tab) keeps working. Nested interactive controls (the
                         mass-address checkbox, the Unsnooze button) use @click.stop, which
                         stops the click from reaching the <a>, so they don't trigger row
                         navigation. -->
                    <div class="hidden md:block hover:bg-gray-50">
                        <a      :href="company.buildCompanyRoute(`alarms/${row.id}`)"
                                class="plain-link grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4">

                            <!-- Cell -->
                            <!-- Alarm Status, point name, zone, & Area -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div    class="flex flex-row items-center h-full">
                                    <!-- Link for checkbox to do mass address -->
                                    <div    v-if="permissions.can('can_mass_alarms_address')"
                                            class="hidden lg:flex h-full items-center justify-center mr-4"
                                            @click.stop>
                                        <input      :id="'mass_address_' + row.id"
                                                    v-model="shouldMassAddress[row.id]"
                                                    :name="'mass_address_' + row.id"
                                                    class="mb-2 focus:ring-blue-500 rounded cursor-pointer"
                                                    type="checkbox"
                                                    @change="setShouldShowMassAddress">
                                    </div>

                                    <!-- Icon for Alarm status -->
                                    <div>
                                        <span   v-if="row.status === 'active'"
                                                :class="iconAlarmActive"
                                        ></span>
                                        <span   v-else-if="row.status === 'snoozed'"
                                                :class="iconAlarmSnoozed"
                                        ></span>
                                        <span   v-else-if="row.status === 'resolved'"
                                                :class="iconAlarmResolved"
                                        ></span>
                                    </div>

                                    <!-- Point, Area, Zone Name. -->
                                    <div class="flex flex-col content-start ml-4">
                                        <span class="font-semibold">
                                            <!-- Point Name -->
                                            {{ row.name }}

                                            <!-- Zone Containing Point -->
                                            <span class="text-gray-500 font-base hidden md:inline"> - in {{ row.zone }}</span>

                                            <!-- Show if alarm is affected by Alarm Generation Schedule -->
                                            <span v-if="row.alarm_generation_schedule_label"
                                                  :title="`Alarm generation schedule: ${row.alarm_generation_schedule_label}`"
                                                  class="lnr icon-moon text-yellow-600 ml-2"></span>
                                        </span>
                                        <!-- Point Area -->
                                        <span class="text-gray-500 hidden md:inline">{{ row.area }}</span>

                                        <!-- @click.stop keeps this button from triggering the row's link navigation. -->
                                        <button v-if="row.status === 'snoozed'"
                                                type="button"
                                                @click.stop="goToUnsnooze(row)"
                                                class="mt-1 self-start px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-500 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300">
                                            Unsnooze
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Alarm Reading & Type -->
                            <div class="inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold">{{ row.reading }}</span>
                                    <span class="text-gray-500">{{ row.time }}</span>
                                </div>
                            </div>

                            <!-- Status -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                <span class="font-semibold">
                                    {{ alarmStatus(row) }}
                                </span>
                                    <span class="text-gray-500">{{ alarmTime(row) }}</span>
                                </div>
                            </div>

                            <!-- Last Action -->
                            <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                                <div class="flex flex-col">
                                    <span class="font-semibold ">
                                        <span :class="getBadgeColor(row.last_comment)">
                                            {{ getLastComment(row)}}
                                        </span>
                                        by {{ getLastCommentUser(row) }}
                                    </span>
                                    <span class="text-gray-500">{{ row.last_comment_time }}</span>
                                </div>
                            </div>
                        </a>
                    </div>
                </li>
                <li v-else>
                    <div class="flex flex-row items-center justify-center py-4">
                        <span :class="iconError"></span>
                        <span v-if="activeHeaderFilter === 'active-snoozed'" class="text-grey-500 text-base font-medium">No Current Alarms</span>
                        <span v-else-if="activeHeaderFilter === 'active'" class="text-grey-500 text-base font-medium">No Active Alarms</span>
                        <span v-else class="text-grey-500 text-base font-medium">No Alarm History</span>
                    </div>
                </li>
            </ul>
            <pagination     v-if="!loading"
                            :num-pages="pageInfo.pages"
                            v-model:page="page"
                            @update:page="updatePage($event)"
            ></pagination>
        </div>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import { iconNamesMixin } from "@/mixins/IconNamesMixin";
import axios from "axios"
import { isNil } from "lodash";
import SlideOverPanel from "@/components/elements/SlideOverPanel";
import Pagination from "@/components/Pagination";
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect";
import InlineTabHeader from "@/components/elements/InlineTabHeader";
import FloatingInput from "@/components/elements/FloatingInput";
import DateTimePicker from "@/components/Reports/VueDatePicker";
import JsonExcel from '@/components/JsonExcel'
import {Features} from "@/mixins/AuthorizationHelpers/Features";
import SonicuButton from "@/components/elements/buttons/SonicuButton.vue";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary.vue";
import AlarmAddress from "@/components/alarmAddress.vue";
import BannerError from "@/components/Banners/BannerError.vue";
import Select2 from "./elements/select2.vue";
import Select2Group from "./elements/select2-group.vue";

const SEARCH_DELAY = 250

export default {
    name: "AlarmsTable",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        Select2Group,
        Select2,
        AlarmAddress,
        BannerError,
        ButtonBluePrimary,
        ButtonBlueSecondary,
        SonicuButton,
        DateTimePicker, FloatingInput, ItemsPerPageSelect, Pagination,
        SlideOverPanel, InlineTabHeader, 'download-excel' : JsonExcel
    },
    props: {
        minDate: String,
        timezone: String,
        areas: Object,
        areasByZones: Object,
        requireComment: {
            type: Boolean,
            default: false,
        },
        maxSnoozeDays: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            data: [],
            modalAlarm: null,
            showModal: false,
            itemsPerPage: '10',
            search: '',
            pageInfo: {
                pages: 1,
                from: 1,
                to: 1,
                total: 1,
            },
            page: 1,
            sortBy: 'reading',
            sortDesc: true,
            loadFunction: null,
            startTime: null,
            endTime: null,
            _zoneIds: null,
            _zoneIdByArea: null,

            // Inline Tab
            filterHeaders: [
                {
                    text: 'Alarms',
                    event: 'active-snoozed',
                },
                {
                    text: 'Active',
                    event: 'active',
                },
                {
                    text: 'Resolved',
                    event: 'resolved',
                },
            ],
            showActive: true,
            showSnoozed: true,
            showResolved: false,
            showOffline: true,
            showThreshold: true,
            showValidation: true,
            showMaintenance: true,
            excludeAutoresolved: false,
            activeHeaderFilter: 'active-snoozed',
            csvFields: {
                'Area' : 'area',
                'Zone' : 'zone',
                'Device Name' : 'name',
                'Serial Number' : 'serial',
                'Alarm Time' : 'time_export_readable',
                'Resolved By' : 'resolved_by',
                'Resolved Time' : 'silenced_at_readable',
                'Alarm Reading' : 'reading',
                'Acknowledge Comment' : 'silence_comment',
            },
            exporting: false,
            showMassAddressSideOver: false,
            savingMassAddress: false,
            massAddressErrors: {},
            massAddressAll: false,
            showMassAddressButton: false,
            shouldMassAddress: {},
            _namedAreasByZones: {},
        }
    },
    created() {
        this.loadFunction = _.debounce(() => {
            this.loading = true
            let startTime = ! this.isInvalidDate(this.startTime)
                ? this.startTime.toISOString()
                : null
            let endTime = ! this.isInvalidDate(this.endTime)
                ? this.endTime.toISOString()
                : null

            let params = {
                page: this.page,
                perPage: this.itemsPerPage,
                search: this.search,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc,
                showActive: this.showActive,
                showSnoozed: this.showSnoozed,
                showResolved: this.showResolved,
                showOffline: this.showOffline,
                zoneIds: this.$data._zoneIds,
                showThreshold: this.showThreshold,
                showValidation: this.showValidation,
                showMaintenance: this.showMaintenance,
                excludeAutoresolved: this.excludeAutoresolved,
                startTime: startTime,
                endTime: endTime,
            }
            this.storeUrl(params)
            this.massAddressAll = false
            axios.get(this.company.buildCompanyApiInternalRoute('alarms/'), {
                params: params
            }).then(response => {
                this.loading = false
                this.data = response.data.data !== undefined ? response.data.data : []
                this.shouldMassAddress = {}
                _.each(this.data, (row, index) => {
                    this.shouldMassAddress[row.id] = false
                })
                if (response.data.meta) {
                    this.pageInfo.pages = response.data.meta.last_page
                    this.pageInfo.from = response.data.meta.from
                    this.pageInfo.to = response.data.meta.to
                    this.pageInfo.total = response.data.meta.total
                }
                else {
                    this.pageInfo = {
                        pages: 1,
                        from: 0,
                        to: 0,
                        total: 0,
                    }
                }
                if (this.page > this.pageInfo.pages) {
                    this.page = this.pageInfo.pages
                    this.loadData()
                }

                this.setupNamedAreasByZones()

            }).catch(error => {
                this.loading = false
                this.data = []
            })
        }, SEARCH_DELAY)
        
        // Change the start date to created date of the company
        this.startTime = new Date(this.minDate)
    },
    mounted() {
        this.readUrl()
        window.addEventListener('popstate', this.handleBackButton)
        this.loadData()
    },
    methods: {
        loadData() {
            // this.data = []
            this.loadFunction()
        },
        // This is cool, makes sure the request is inline, only issue is it could hold up the page for long requests
        // TODO: Make it so if the export has more than some number N alarms, we should instead flash a notification
        //      that it is a large request & we will email them the csv when completed.
        async exportData() {
            this.exporting = true
            let params = {
                page: 1,
                perPage: this.pageInfo.total.toString(),
                search: this.search,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc,
                showActive: this.showActive,
                showSnoozed: this.showSnoozed,
                showResolved: this.showResolved,
                excludeAutoresolved: this.excludeAutoresolved,
                startTime: this.startTime !== null ? `${this.startTime.toISOString()}` : null,
                endTime: this.endTime !== null ? `${this.endTime.toISOString()}` : null,
            }

            let newData = []
            const response = await axios.get(this.company.buildCompanyApiInternalRoute('alarms/'), {
                params: params
            })

            newData = response.data.data !== undefined ? response.data.data : []
            this.exporting = false

            return newData
        },
        showMassAddress() {
            this.massAddressErrors = {}
            this.showMassAddressSideOver = true
        },
        massAddress(action, event) {
            // Set up the parameters, filtering down to the list of alarms selected
            let params = {
                alarmsToAddress: this.data.filter(alarm => this.shouldMassAddress[alarm.id]),
                action: action,
            }
            params = _.merge(params, event)
            // Convert the alarms to address to a list of alarm ids.
            _.each(params.alarmsToAddress, (alarm, index) => {
                params.alarmsToAddress[index] = alarm.id
            })

            // Clear any prior errors before re-submitting.
            this.massAddressErrors = {}

            // Submit to a mass address alarm controller. Only close the slide over and
            // refresh once the server has applied the action, otherwise loadData() races
            // the PATCH and reloads the pre-action state (e.g. rows not showing snoozed).
            axios.patch(this.company.buildCompanyApiInternalRoute('alarms/mass-address'), params)
                .then(() => {
                    this.showMassAddressSideOver = false
                    this.loadData()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422 && error.response.data.errors) {
                        this.massAddressErrors = error.response.data.errors
                    } else {
                        this.massAddressErrors = { Error: 'Unable to address the selected alarms. Please try again.' }
                    }
                })
        },
        setShouldShowMassAddress() {
            let oneOrMoreSelected = false
            _.each(this.shouldMassAddress, (shouldAlarm, index) => {
                if (this.shouldMassAddress[index]) {
                    oneOrMoreSelected = true
                }
            })

            this.showMassAddressButton = oneOrMoreSelected
        },
        updatePage(page) {
            this.page = page
            this.loadData()
        },
        alarmStatus(alarm) {
            if (alarm.silenced_at){
                return 'Resolved'
            }
            else if (alarm.snooze_until) {

                return this.isSilenced(alarm) ? 'Silenced' : 'Snoozed'
            }
            else {
                return 'Active'
            }
        },
        isSilenced(alarm) {
            if (!alarm.snooze_until) {
                return false
            }

            let silencedTime = alarm.snooze_until.split(' years')
            let silenced = silencedTime.length > 1
            if (silencedTime.length > 1) {
                silencedTime = Number(silencedTime[0])
            }
            return silencedTime >= 2
        },
        alarmTime(alarm) {
            if (alarm.silenced_at) {
                return `${alarm.silenced_at_diff}`
            }
            else if (alarm.snooze_until) {
                return this.isSilenced(alarm) ? `` : `For ${alarm.snooze_until_diff}`
            }
            else {
                return `For ${alarm.time_diff}`
            }
        },
        getLastComment(alarm) {
            if (isNil(alarm.last_comment) || !alarm.last_comment.user_id) {
                return 'Generated'
            }
            if (this.isSilenced(alarm)) {
                return 'Silenced'
            }
            return alarm.last_comment.comment_type
        },
        getLastCommentUser(alarm) {
            let comment = alarm.last_comment
            if (isNil(comment)) {
                return 'SoniCloud'
            }
            if(typeof comment.user_name === 'string' || comment.user_name instanceof String) {
                return comment.user_name
            }
            if (!isNil(comment.user)) {
                return comment.user.display_name
            }
            else if (!isNil(comment.user_name)) {
                return comment.user_name
            }
            return comment.data?.user ?? 'SoniCloud'
        },
        getLastCommentActionText(alarm){
            let comment = alarm.last_comment
            if(isNil(comment) || comment.user_id === null){
                return 'Generated';
            } else {
                return comment.comment_type;
            }
        },
        showRowModal(alarm) {
            this.modalAlarm = alarm
            this.showModal = true
        },
        unsnoozeLink(row) {
            return this.company.buildCompanyRoute(`alarms/${row.id}`) + '?action=cancel-snooze'
        },
        goToUnsnooze(row) {
            window.location.href = this.unsnoozeLink(row)
        },
        updateSort(by) {
            if (this.sortBy === by) {
                this.sortBy = this.sortDesc ? '' : this.sortBy
                this.sortDesc = !this.sortDesc
            }
            else {
                this.sortBy = by
                this.sortDesc = false
            }
            this.loadData()
        },
        updateCurrentFilter(currentFilter) {
            this.activeHeaderFilter = currentFilter
            this.page = 1
            this.showActive = currentFilter === 'active' ||
                currentFilter === 'active-snoozed'
            this.showSnoozed = currentFilter === 'active-snoozed'
            this.showResolved = currentFilter === 'resolved'
            if (currentFilter !== 'resolved') {
                this.excludeAutoresolved = false
            }
            this.loadData()
        },
        showOfflineClicked() {
            this.showOffline = !this.showOffline
            this.loadData()
        },
        showThresholdClicked() {
            this.showThreshold = !this.showThreshold
            this.loadData()
        },
        showValidationClicked() {
            this.showValidation = !this.showValidation
            this.loadData()
        },
        showMaintenanceClicked() {
            this.showMaintenance = !this.showMaintenance
            this.loadData()
        },
        excludeAutoresolvedClicked() {
            this.excludeAutoresolved = !this.excludeAutoresolved
            this.loadData()
        },
        getBadgeColor(comment){
            if(isNil(comment) || comment.badge_color === 'gray'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-gray-100 text-gray-800';
            }
            if(comment.badge_color === 'red'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-red-100 text-red-800';
            }
            if(comment.badge_color === 'green'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-green-100 text-green-800';
            }
            if(comment.badge_color === 'yellow'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-yellow-100 text-yellow-800';
            }
            if(comment.badge_color === 'blue'){
                return 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium leading-5 bg-blue-100 text-blue-800';
            }
        },
        setupNamedAreasByZones() {
            // This is for the purposes of the options list of the select2-group.
            //   It is using the areasByZones list as its options, but that is keyed by areas for obvious reasons.
            //   We will however locally make a copy that is keyed by the area name for user purposes
            let namedAreasByZones = {}
            _.each(this.areasByZones, (zones, area) => {
                namedAreasByZones[this.areas[area]] = zones
            })

            this._namedAreasByZones = namedAreasByZones
        },
        handleBackButton() {
            this.readUrl()
            this.loadData()
        },
        readUrl() {
            const urlSearchParams = new URLSearchParams(window.location.search)
            const data = {}

            urlSearchParams.forEach((value, key) => {
                data[key] = value
            })

            this.page = !isNil(data['page']) ? Number(data['page']) : this.page
            this.itemsPerPage = !isNil(data['perPage']) ? data['perPage'] : this.itemsPerPage
            this.search = !isNil(data['search']) ? data['search'] : this.search
            this.showActive = !isNil(data['showActive']) ? data['showActive'] === 'true' : this.showActive
            this.showResolved = !isNil(data['showResolved']) ? data['showResolved'] === 'true' : this.showResolved
            this.showSnoozed = !isNil(data['showSnoozed']) ? data['showSnoozed'] === 'true' : this.showSnoozed
            this.showOffline = !isNil(data['showOffline']) ? data['showOffline'] === 'true' : this.showOffline
            this.showThreshold = !isNil(data['showThreshold']) ? data['showThreshold'] === 'true' : this.showThreshold
            this.showValidation = !isNil(data['showValidation']) ? data['showValidation'] === 'true' : this.showValidation
            this.showMaintenance = !isNil(data['showMaintenance']) ? data['showMaintenance'] === 'true' : this.showMaintenance
            this.sortBy = !isNil(data['sortBy']) ? data['sortBy'] : this.sortBy
            this.sortDesc = !isNil(data['sortDesc']) ? data['sortDesc'] === 'true' : this.sortDesc
            this.startTime = 'startTime' in data && data['startTime'] !== "null" ? new Date(data['startTime']) : null
            this.endTime = 'startTime' in data && data['endTime'] !== "null" ? new Date(data['endTime']) : null
            this.activeHeaderFilter = this.showResolved === true ? 'resolved' :
                (this.showSnoozed !== true && this.showActive === true ? 'active' : 'active-snoozed')
            this.excludeAutoresolved = this.activeHeaderFilter === 'resolved' && !isNil(data['excludeAutoresolved'])
                ? data['excludeAutoresolved'] === 'true'
                : false
        },
        storeUrl(data) {
            const urlSearchParams = new URLSearchParams()

            Object.keys(data).forEach(key => {
                urlSearchParams.set(key, data[key])
            })

            window.history.pushState(
                window.location.hostname,
                "Table",
                this.company.buildCompanyRoute('alarms?') + urlSearchParams.toString()
            )
        },
        isInvalidDate(date) {
            return ! _.isDate(date) || isNaN(date.getTime())
        },
      isNil,
    },
    watch: {
        itemsPerPage() {
            this.loadData()
        },
        search() {
            this.loadData()
        },
        startTime() {
            this.loadData()
        },
        endTime() {
            this.loadData()
        },
        _zoneIds(val, old) {
            if (val === old) {
                return
            }

            this.$data._zoneIdByArea = null
            this.loadData()
        },
        _zoneIdByArea(val, old) {
            if (val === old) {
                return
            }

            this.$data._zoneIds = null
            this.loadData()
        },
        massAddressAll() {
            let value = this.massAddressAll

            // For each alarm, mark address alarm checkbox to match
            _.each(this.shouldMassAddress, (val, key) => {
                this.shouldMassAddress[key] = value
            })
        }
    },
    computed: {
        hasValidationAlarms(){
          return this.featureLevel.hasFeature(Features.VALIDATION_ALARM);
        },
        hasMaintenanceAlarms(){
          return this.featureLevel.hasFeature(Features.POINT_MAINTENANCE_MODE);
        },
        // Drives the "replaces the current snooze" note in the mass snooze panel. Maintenance
        // alarms are excluded because the mass snooze skips them rather than replacing them.
        anySelectedSnoozed() {
            return this.data.some(alarm => this.shouldMassAddress[alarm.id]
                && alarm.status === 'snoozed'
                && alarm.type !== 'Maintenance')
        },
    },
    unmounted() {
        window.removeEventListener('popstate', this.handleBackButton)
    }
}
</script>

<style scoped>
input[placeholder="Search"],
select {
    border-radius: 0.375rem !important;
}

button input[type="checkbox"] {
    vertical-align: unset;
    display: block;
    margin-top: 0;
    margin-bottom: 0;
}

.date-range-inputs :deep(input) {
    border: none;
    border-radius: 0;
    outline: none;
    box-shadow: none;
    font-size: 0.875rem;
    line-height: 1.25rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    color: rgb(63 63 70);
}

.date-range-inputs :deep(input::placeholder) {
    color: rgb(161 161 170);
}

.location-select :deep(.select2-container) {
    width: 100% !important;
    min-width: 12rem;
}

.location-select :deep(.select2-selection--multiple) {
    white-space: nowrap;
    overflow: hidden;
    flex-wrap: nowrap;
    font-size: 0.875rem;
    border-radius: 0.375rem !important;
    min-height: 34px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
}

.location-select :deep(.select2-selection__placeholder) {
    color: rgb(161 161 170);
    font-size: 0.875rem;
}

.location-select :deep(.select2-search__field) {
    font-size: 0.875rem;
}

.location-select :deep(.select2-search__field::placeholder) {
    color: rgb(161 161 170);
}

.location-select :deep(.select2-selection__rendered) {
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;
}
</style>
